export function parseJsonSafe(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const PROVIDER_LABELS = {
  meta: "Meta Cloud API",
  gupshup: "Gupshup",
  apifacil: "API Fácil (QR)",
  meta_messaging: "Messenger/Instagram",
  google_ads: "Google Ads",
};

export function providerLabel(provider) {
  if (!provider) return null;
  return PROVIDER_LABELS[String(provider).toLowerCase()] || String(provider);
}

export function isGoogleStarterAttribution(attribution) {
  if (!attribution) return false;
  const sourceType = String(attribution.sourceType || attribution.source_type || "").toLowerCase();
  const sourceId = String(attribution.sourceId || attribution.source_id || "");
  const provider = String(attribution.provider || "").toLowerCase();
  return (
    sourceType === "starter_message" ||
    sourceId.startsWith("starter:") ||
    provider === "google_ads" ||
    attribution.adOriginId != null ||
    attribution.ad_origin_id != null
  );
}

export function getWhatsappContextFromMessage(message) {
  if (message?.whatsappContext && typeof message.whatsappContext === "object") {
    return message.whatsappContext;
  }
  const root = parseJsonSafe(message?.json_whatsapp);
  if (!root) return null;
  const ctx = root.xpro_whatsapp_context;
  if (!ctx || typeof ctx !== "object") {
    const waRef = root.xpro_whatsapp_referral;
    const thread = root.xpro_thread;
    const group = root.xpro_group;
    if (!waRef && !thread?.referredProduct && !group) return null;
    return {
      provider: waRef?.provider || null,
      eventType: waRef?.fromAd ? "ctwa_referral" : group ? "group_message" : null,
      isCtwaReferral: Boolean(waRef?.fromAd),
      isProductInquiry: Boolean(thread?.referredProduct),
      referral: waRef || null,
      referredProduct: thread?.referredProduct || null,
      group: group || null,
    };
  }
  const referral = root.xpro_whatsapp_referral || ctx.referral || null;
  return {
    provider: ctx.provider || referral?.provider || null,
    eventType: ctx.eventType || null,
    isCtwaReferral: ctx.eventType === "ctwa_referral" || Boolean(referral?.fromAd),
    isStatusAd: Boolean(referral?.isStatusAd),
    isProductInquiry: ctx.eventType === "product_inquiry" || Boolean(ctx.context?.referredProduct),
    isCatalogOrder: ctx.eventType === "catalog_order" || Boolean(ctx.order),
    isGroupMessage: ctx.eventType === "group_message" || Boolean(ctx.group || root.xpro_group),
    isSystem: ctx.eventType === "system",
    isUnsupported: ctx.eventType === "unsupported",
    referral,
    referredProduct: ctx.context?.referredProduct || root.xpro_thread?.referredProduct || null,
    order: ctx.order || null,
    group: ctx.group || root.xpro_group || null,
    interactive: ctx.interactive || null,
    addressing: ctx.addressing || null,
  };
}

export function whatsappContextLabel(ctx) {
  if (!ctx) return null;
  if (ctx.isCtwaReferral && isGoogleStarterAttribution(ctx.referral)) {
    return "Entrada via Google Ads (mensagem)";
  }
  if (ctx.isCtwaReferral && ctx.isStatusAd) return "Anúncio no Status do WhatsApp";
  if (ctx.isCtwaReferral) return "Entrada via anúncio (CTWA)";
  if (ctx.isProductInquiry) return "Produto do catálogo";
  if (ctx.isCatalogOrder) return "Pedido do catálogo";
  if (ctx.isGroupMessage) return "Mensagem de grupo";
  if (ctx.isSystem) return "Mensagem de sistema";
  if (ctx.isUnsupported) return "Mensagem não suportada";
  if (ctx.eventType === "location") return "Localização";
  return null;
}

export function formatWhatsappAttributionPanel(attribution) {
  if (!attribution) return null;
  const googleStarter = isGoogleStarterAttribution(attribution);
  const rows = [];
  if (attribution.provider) {
    rows.push({
      label: "Provedor",
      value: googleStarter ? "Google Ads (mensagem inicial)" : providerLabel(attribution.provider),
    });
  }
  if (attribution.headline) {
    rows.push({
      label: googleStarter ? "Origem / campanha" : "Título do anúncio",
      value: attribution.headline,
    });
  }
  if (attribution.body) {
    rows.push({
      label: googleStarter ? "Mensagem inicial" : "Descrição",
      value: attribution.body,
    });
  }
  if (attribution.sourceType && !googleStarter) {
    rows.push({ label: "Tipo de origem", value: attribution.sourceType });
  }
  if (googleStarter) {
    rows.push({
      label: "Como foi identificado",
      value: "O cliente enviou a mensagem combinada do anúncio do Google",
    });
  }
  if (!googleStarter && (attribution.sourceId || attribution.adId)) {
    rows.push({
      label: "ID do anúncio",
      value: attribution.sourceId || attribution.adId,
      copyable: true,
    });
  }
  if (attribution.ctwaClid) rows.push({ label: "CTWA click ID", value: attribution.ctwaClid, copyable: true });
  if (attribution.sourceUrl) rows.push({ label: "URL do anúncio", value: attribution.sourceUrl, link: true });
  if (attribution.uncertain) rows.push({ label: "Confiança", value: "Origem inferida (provedor não garante CTWA)" });
  if (attribution.group?.name || attribution.group?.id) {
    rows.push({
      label: "Grupo",
      value: attribution.group.name
        ? `${attribution.group.name}${attribution.group.id ? ` (${attribution.group.id})` : ""}`
        : attribution.group.id,
    });
  }
  if (attribution.automaticEvents) {
    rows.push({ label: "Eventos automáticos", value: "Registrado (compra/lead detectado pela Meta)" });
  }
  return {
    rows,
    imageUrl: attribution.imageUrl || attribution.thumbnailUrl || null,
    videoUrl: attribution.videoUrl || null,
    fromAd: Boolean(attribution.fromAd),
  };
}

export function formatMetaReferralLabel(metaReferral) {
  if (!metaReferral?.fromAd) return null;
  if (isGoogleStarterAttribution(metaReferral)) {
    const headline = metaReferral.headline ? String(metaReferral.headline).trim() : "";
    return headline ? `Google Ads: ${headline}` : "Google Ads (mensagem)";
  }
  const headline = metaReferral.headline ? String(metaReferral.headline).trim() : "";
  if (headline) return `Anúncio: ${headline}`;
  return "Entrada via anúncio";
}

export function formatWhatsappAttributionChip(attribution) {
  if (!attribution) return null;
  if (attribution.fromAd) {
    if (isGoogleStarterAttribution(attribution)) {
      const headline = attribution.headline ? String(attribution.headline).trim() : "";
      return headline ? `Google: ${headline}` : "Google Ads (mensagem)";
    }
    const headline = attribution.headline ? String(attribution.headline).trim() : "";
    if (headline) return `Anúncio: ${headline}`;
    if (attribution.isStatusAd) return "Anúncio no Status";
    return "Entrada via anúncio";
  }
  if (attribution.group?.name) return `Grupo: ${attribution.group.name}`;
  if (attribution.automaticEvents) return "Evento automático (CTWA)";
  return null;
}
